import { formatQuotePrice } from "@/domain/money";
import {
  calculateTargetExitPercentages,
  riskReward,
  shouldSellLoss,
  shouldSellProfit,
} from "@/domain/trade";
import type { Trade } from "@/domain/types";

/**
 * How far past a line the price has run, as a percentage of that line.
 *
 * Measured against the line rather than the current price, because the
 * question is "how far beyond the level I chose?" — and the level is the thing
 * that was chosen.
 */
function percentBeyond(currentPrice: number, line: number): number {
  if (line === 0) return 0;
  return Math.abs(Number((((currentPrice - line) / line) * 100).toFixed(1)));
}

/**
 * Where the price stands between the stop loss and the profit target.
 *
 * This is the app's signature element, and the thing its own marketing
 * promised — "visualize targets" — that the 2020 version never drew. It showed
 * a price chart with reference lines, which answers "what has this coin been
 * doing?" The question you open the app to ask is "where do I stand relative
 * to the plan I committed to?", and that is a position between two declared
 * lines.
 *
 * Rendered on the yellow field: in this design, yellow means "your plan".
 */
export function Bracket({
  trade,
  currentPrice,
  quoteCurrency,
}: {
  trade: Trade;
  /** Omitted on a closed trade, or when the live lookup failed. */
  currentPrice?: number;
  quoteCurrency: string;
}) {
  const { target, buy, tradeType } = trade;

  /*
    The axis spans everything it has to show, not just the plan.

    It used to run stop→target and clamp, which meant any price outside the
    plan pinned to an end — a position 133% past its target rendered pixel-for
    pixel identically to one sitting exactly on it, and updating the price
    moved nothing. The one moment the picture matters most is the moment it
    stopped moving.

    Ordering by value rather than by role is what lets a SHORT work: its stop
    sits *above* its target, so a role-ordered axis renders inside out.
  */
  const values = [target.profit, target.stopLoss, buy.price];
  if (currentPrice != null) values.push(currentPrice);

  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low || 1;
  const at = (price: number) => Math.min(1, Math.max(0, (price - low) / span)) * 100;

  const percentages = calculateTargetExitPercentages(trade);
  const rr = riskReward(buy.price, target);

  const pastTarget = currentPrice != null && shouldSellProfit(trade, currentPrice);
  const pastStop = currentPrice != null && shouldSellLoss(trade, currentPrice);

  /**
   * Labels are centred on their marks, so neighbours that land close together
   * overlap. Widening the axis to fit an out-of-range price makes that far more
   * likely — a stop and an entry a couple of percent apart become a pile once
   * the price runs away from both.
   *
   * Crowded marks move to the *other side of the rail* rather than a lower row
   * beneath it. Stacking below only bought the height of one row, about 24px,
   * against a label block nearer 55px — so the labels still touched. Flipping
   * to the empty space above separates them completely, whatever their heights.
   *
   * Threshold is in axis percent, judged against the widest label ("Target"
   * over "$62,993").
   */
  const CROWDED_WITHIN = 14;

  const marks = [
    {
      key: "stop",
      price: target.stopLoss,
      label: "Stop",
      sub: `${percentages.stopLoss}%`,
    },
    { key: "entry", price: buy.price, label: "In", sub: null },
    {
      key: "target",
      price: target.profit,
      label: "Target",
      sub: `+${percentages.profit}%`,
    },
  ]
    .map((mark) => ({ ...mark, position: at(mark.price), above: false }))
    .sort((a, b) => a.position - b.position);

  for (let i = 1; i < marks.length; i++) {
    const mark = marks[i]!;
    const previous = marks[i - 1]!;
    // Only flips against a neighbour still below, so three tightly packed marks
    // alternate rather than all piling on the same side.
    if (!previous.above && mark.position - previous.position < CROWDED_WITHIN) {
      mark.above = true;
    }
  }

  // Labels above need headroom the eyebrow would otherwise occupy.
  const anyAbove = marks.some((mark) => mark.above);

  return (
    <section className="field-accent">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <p className="eyebrow text-ink/60!">Your plan</p>

        {/*
          Inset the rail rather than padding the container. The marks are
          absolutely positioned at 0% and 100%, and percentages resolve against
          the padding box — so padding would not move them inward. Each label is
          centred on its mark, so roughly half a label's width has to clear each
          end.
        */}
        <div
          className={`relative mx-6 h-14 sm:mx-10 ${anyAbove ? "mt-24" : "mt-10"}`}
          aria-hidden="true"
        >
          <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-ink" />

          {marks.map((mark) => (
            <div
              key={mark.key}
              className="absolute top-1/2 -translate-y-1/2"
              style={{ left: `${mark.position}%` }}
            >
              <span className="block h-5 w-0.5 -translate-x-1/2 bg-ink" />
              <span
                data-side={mark.above ? "above" : "below"}
                className={`absolute left-0 -translate-x-1/2 text-center whitespace-nowrap ${
                  mark.above ? "bottom-6" : "top-6"
                }`}
              >
                <span className="block font-mono text-[0.6rem] font-medium tracking-[0.2em] uppercase">
                  {mark.label}
                </span>
                <span className="figure block text-sm font-semibold">
                  {formatQuotePrice(mark.price, quoteCurrency, trade.assetType)}
                </span>
                {mark.sub && (
                  <span className="figure block text-[0.65rem] text-ink/55">{mark.sub}</span>
                )}
              </span>
            </div>
          ))}

          {currentPrice != null && (
            <div
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${at(currentPrice)}%` }}
            >
              <span className="block size-5 rotate-45 border-2 border-accent bg-ink" />
            </div>
          )}
        </div>

        <p className="mt-16 max-w-lg font-sans text-sm font-medium">
          {currentPrice == null ? (
            <>
              Risk to reward, {rr ?? "—"} to 1. Live price unavailable, so your position on this
              line is not shown.
            </>
          ) : pastTarget ? (
            /*
              Past the plan, not still working towards it. The in-range wording
              below reads "X% from your target", which on an overshoot said you
              had further to go when you were already through — a position 133%
              past its target reported "57.1% from your target".
            */
            <>
              <strong>Past your target</strong> by{" "}
              {percentBeyond(currentPrice, target.profit)}%. The plan said get out here.
            </>
          ) : pastStop ? (
            <>
              <strong>Past your stop</strong> by {percentBeyond(currentPrice, target.stopLoss)}%.
              The plan said get out here.
            </>
          ) : (
            <>
              {Math.abs(
                Number((((target.profit - currentPrice) / currentPrice) * 100).toFixed(1)),
              )}
              % from your target, {" "}
              {Math.abs(
                Number((((currentPrice - target.stopLoss) / currentPrice) * 100).toFixed(1)),
              )}
              % {tradeType === "LONG" ? "above" : "below"} your stop. Risk to reward, {rr ?? "—"}{" "}
              to 1.
            </>
          )}
        </p>

        <p className="sr-only">
          Stop {formatQuotePrice(target.stopLoss, quoteCurrency, trade.assetType)}, entry{" "}
          {formatQuotePrice(buy.price, quoteCurrency, trade.assetType)}, target{" "}
          {formatQuotePrice(target.profit, quoteCurrency, trade.assetType)}
          {currentPrice != null &&
            `, currently ${formatQuotePrice(currentPrice, quoteCurrency, trade.assetType)}`}.
        </p>
      </div>
    </section>
  );
}
